<script setup lang="ts">
import { computed, ComputedRef, onMounted, ref, Ref, toRefs, watch } from 'vue';
import {
  ContentFilters,
  ContentQuery,
  ContentReference,
  isHistoryCollection,
  parseReference,
  stringifyReference,
  useCollectionContents,
  useCollectionInternalSearch,
  useContentCollection,
} from '@ilteducation/content-collections';
import {
  ContentViewTopBar,
  DialogDrawer,
  SearchInput,
  SelectedContentsPanel,
  type SelectionAction,
  SelectionBottomSheet,
  SelectModeButton,
  useDialogFocus,
  ContentTypes,
} from '@ilteducation/components';
import AddContent from '@/components/collections/AddContent.vue';
import { useLocale } from '@/plugins/translation';
import useSubscriptions from '@/foundations/helpers/use-subscriptions';
import { useDisplay } from 'vuetify';
import type { ContentWithStringReference } from '@/components/collections/ContentList.vue';
import ContentList from '@/components/collections/ContentList.vue';
import type { Content } from '@ilteducation/content-registry-types';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import AddContentDialogTopbar from '@/components/collections/AddContentDialogTopbar.vue';

type Props = {
  contentCollectionId: string;
  showShareDialog: boolean;
};
const props = withDefaults(defineProps<Props>(), { showShareDialog: true });

const { contentCollectionId } = toRefs(props);

const emit = defineEmits<{
  (e: 'onUpdateContent'): void;
  (e: 'onRemoveContent'): void;
}>();

const defaultFilters: ContentFilters = {
  ages: [],
  audioLanguages: [],
  subscription: '',
  textLanguages: '',
};

const { t, l } = useLocale();
const { xs, smAndDown } = useDisplay();
const subscriptions = useSubscriptions();
const filters = computed(() => ({ ...defaultFilters, subscription: subscriptions.value[0].type }));
const { collection, updateContents, removeContents, selectedContents, isOwner } =
  useContentCollection(contentCollectionId);
// Which content type that is currently searched for in AddContent. This variable is not used when searching just on the collection page.
const searchContentType = ref<ContentTypes>(ContentTypes.Video);

const { next, contents, isLoading, hasMore } = useCollectionContents(
  contentCollectionId,
  {} as Ref<ContentQuery>,
  filters,
);

const isTopSelectPanelVisible = computed(() => isOwner.value);
const isAddContentDialogActive = ref<boolean>(false);
const { rememberFocus } = useDialogFocus([isAddContentDialogActive]);
const isProcessing = ref<boolean>(false);
const showSelects = ref<boolean>(false);
const search = ref<string>('');
const gridElement = ref(null);
const filteredContent = useCollectionInternalSearch(search, contents) as ComputedRef<Content[]>;

const filteredContentWithReference = computed<ContentWithStringReference[]>(() => {
  if (collection.value?.contents.type !== 'ContentsByReferences') {
    return [];
  }
  return collection.value?.contents.references
    .map((ref) => {
      const content = filteredContent.value.find((c) => c.id === ref.id);
      if (content) {
        return {
          ...content,
          stringReference: stringifyReference(ref),
        };
      }
      return null;
    })
    .filter((c): c is ContentWithStringReference => c !== null);
});

const onClickAddContent = () => {
  rememberFocus();
  isAddContentDialogActive.value = true;
};

const loadMore = () => {
  if (hasMore.value && !isLoading.value) {
    next();
  }
};

watch(filteredContent, () => {
  loadMore();
});

onMounted(() => {
  loadMore();
});

const updateContent = async (contents: ContentReference[]) => {
  isProcessing.value = true;
  await updateContents(contents);

  isProcessing.value = false;
  emit('onUpdateContent');
  selectedContents.value = [];
};
const removeContent = async () => {
  if (selectedContents.value.length === 0) {
    return;
  }
  isProcessing.value = true;
  await removeContents(selectedContents.value.map((id) => parseReference(id)));
  isProcessing.value = false;
  emit('onRemoveContent');
  selectedContents.value = [];
};

const panelActions = computed<SelectionAction[]>(() => [
  {
    key: 'remove',
    label: t(l.components.remove_button) as string,
    icon: 'fa-solid fa-trash-can',
    destructive: true,
    onSelect: removeContent,
  },
]);

const isOrdering = ref(false);
const onToggleSelect = () => {
  showSelects.value = !showSelects.value;
  if (!showSelects.value) {
    selectedContents.value = [];
    isOrdering.value = false;
  }
};

const onToggleOrdering = () => {
  isOrdering.value = !isOrdering.value;
};

const historyCollection = computed(() => isHistoryCollection(props.contentCollectionId));

const isAddContentButtonVisible = computed(() => !historyCollection.value && !showSelects.value);
</script>

<template>
  <div class="d-flex flex-column h-100">
    <ContentViewTopBar view-type="list" :view-type-options="['list']">
      <template #top>
        <SearchInput @onSearch="(searchString: string) => (search = searchString)" />
      </template>
    </ContentViewTopBar>
    <div class="pa-5 pb-0 rounded-xl bg-surface-bright flex-grow-1" :class="xs ? 'mb-5' : 'mb-0'">
      <h2 class="d-sr-only">
        {{ t(l.components.collection_content) }}
      </h2>

      <div v-if="isTopSelectPanelVisible" class="d-flex ma-0 mb-4 align-center justify-space-between ga-4">
        <div class="flex-grow-1 d-flex align-center flex-wrap ga-3">
          <v-btn
            v-if="isAddContentButtonVisible"
            data-testid="add-content-btn"
            color="primary"
            rounded="default"
            prepend-icon="mdi-plus"
            class="text-none"
            @click="onClickAddContent"
          >
            {{ t(l.components.add_button) }}
          </v-btn>
          <v-btn
            v-if="showSelects"
            data-testid="order-content-btn"
            class="ml-2 text-on-surface"
            color="surface-dim"
            :size="xs ? 'small' : 'default'"
            flat
            rounded="default"
            @click="onToggleOrdering"
          >
            {{ isOrdering ? t(l.content_collections.button_order_done) : t(l.content_collections.button_order) }}
            <template #prepend>
              <FontAwesomeIcon icon="arrow-down-arrow-up" size="sm"></FontAwesomeIcon>
            </template>
          </v-btn>
          <SelectedContentsPanel
            v-if="showSelects && !smAndDown"
            :selected-content-total="selectedContents.length"
            :show-selected="true"
            :is-disabled="isOrdering || selectedContents.length === 0"
            :actions="panelActions"
            rounded="default"
          />
        </div>
        <SelectModeButton
          :model-value="showSelects"
          rounded="default"
          variant="text"
          color="primary"
          :bordered="false"
          @update:model-value="onToggleSelect"
        />
      </div>
      <SelectionBottomSheet :model-value="showSelects">
        <SelectedContentsPanel
          :card="false"
          :selected-content-total="selectedContents.length"
          :show-selected="true"
          :is-disabled="isOrdering || selectedContents.length === 0"
          :actions="panelActions"
        />
      </SelectionBottomSheet>
      <div
        ref="gridElement"
        class="content-wrapper pb-16 pb-md-0"
        :class="{
          'content-wrapper-without-select-panel': !isTopSelectPanelVisible,
        }"
      >
        <ContentList
          v-model="selectedContents"
          :rounded="true"
          :content-items="filteredContentWithReference"
          :parent-scroll-element="gridElement"
          :is-loading="isLoading || isProcessing"
          :is-managing="showSelects"
          :is-ordering="isOrdering"
          @on-load-content="next"
          @add-content="onClickAddContent"
          @update-content="updateContent"
        >
        </ContentList>
        <div
          :class="{
            selecting: showSelects,
            'position-relative': true,
          }"
        >
          <v-progress-linear
            v-if="isProcessing"
            indeterminate
            color="primary"
            class="position-absolute w-100 mt-n3"
            :aria-label="t(l.label_processing)"
          />
        </div>
      </div>
    </div>
  </div>
  <DialogDrawer
    v-if="collection"
    :full-height="true"
    :model-value="isAddContentDialogActive"
    close-btn-color="on-tertiary-fixed-variant"
    :title-text="t(l.components.message_add_new_content)"
    :has-done-button-to-close="true"
    @close="isAddContentDialogActive = false"
  >
    <template #topbar>
      <AddContentDialogTopbar v-model="searchContentType" @close="isAddContentDialogActive = false" />
    </template>
    <keep-alive>
      <AddContent :content-collection-id="collection.id" :filters="filters" :content-type="searchContentType" />
    </keep-alive>
  </DialogDrawer>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'vuetify/settings' as vuetify;

$topMenuHeight: 64px;
$backButtonHeight: 64px;
$bottomMargin: 12px;
$collectionContentTopBarHeight: 160px;
$collectionContentTopBarWithoutSelectionHeight: 65px;

@function calculateHeight($heightUnit, $ccTopBarHeight) {
  @return calc(
    #{'100' + $heightUnit} - #{$topMenuHeight} - #{$backButtonHeight} - #{$bottomMargin} - #{$ccTopBarHeight}
  );
}

.content-wrapper {
  @media #{map.get(vuetify.$display-breakpoints, 'md-and-up')} {
    overflow: hidden;
    overflow-y: auto;
    position: relative;

    height: calculateHeight('dvh', $collectionContentTopBarHeight);
    &-without-select-panel {
      height: calculateHeight('dvh', $collectionContentTopBarWithoutSelectionHeight);
    }
  }
}
</style>
